import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Euro, Check } from 'lucide-react';
import api, { apiError } from '../../../lib/api';

export function RenameDialog({ conv, onClose, onSave }) {
  const [name, setName] = useState('');
  useEffect(() => { if (conv) setName(conv.name); }, [conv]);
  return (
    <Dialog open={!!conv} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200 max-w-sm">
        <DialogHeader><DialogTitle className="text-white">Renommer la conversation</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <Input data-testid="rename-conv-input" value={name} onChange={e => setName(e.target.value)} className="bg-white/5 border-white/10 text-white h-10" placeholder="Nom affiché / nom unique" />
          <Button data-testid="rename-conv-save" className="w-full bg-indigo-500 hover:bg-indigo-600" onClick={() => name.trim() && onSave(name.trim())}>Enregistrer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const EMOJI_BANK = [
  '😘', '🔥', '😏', '😈', '❤️', '🙈', '😍', '🥰', '😋', '😉',
  '💋', '💦', '🍑', '🍆', '👅', '😜', '🤤', '😩', '🤗', '😇',
  '👀', '✨', '💕', '💖', '💗', '💘', '💝', '💞', '💟', '💌',
  '😴', '🌹', '🍓', '🍒', '🍭', '🍷', '🥂', '🎁', '🌶️', '⭐',
  '😊', '🥲', '😌', '🤭', '😅', '😂', '🤣', '🙃', '😎', '🤩',
  '😻', '😽', '🥵', '🥴', '😵‍💫', '🫦', '👄', '💄', '👙', '🩱',
  '🧡', '💛', '💚', '💙', '💜', '🤍', '🖤', '❣️', '💢', '💫',
  '🤍', '🫶', '👐', '🙌', '👏', '🤙', '✌️', '🤞', '👉', '👈',
  '🍫', '🍰', '🧁', '🍾', '🍸', '🍹', '☕', '🌙', '💎', '👑',
  '🎀', '🦋', '🌸', '🌺', '💐', '🍯', '🫧', '💸', '😳', '🥹',
];

export function EmojiEditDialog({ open, account, onClose, onSave }) {
  const [emojis, setEmojis] = useState([]);
  useEffect(() => { if (open && account) setEmojis(account.quick_emojis || []); }, [open, account]);

  const toggle = (e) => {
    setEmojis(prev => prev.includes(e) ? prev.filter(x => x !== e) : (prev.length < 10 ? [...prev, e] : prev));
  };
  const remove = (i) => setEmojis(prev => prev.filter((_, k) => k !== i));

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200 max-w-md">
        <DialogHeader><DialogTitle className="text-white">Emojis rapides — {account?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Sélectionnés ({emojis.length}/10)</div>
          <div className="min-h-[44px] rounded-xl border border-white/10 bg-white/[0.03] p-2 flex items-center gap-1.5 flex-wrap" data-testid="emoji-selected-list">
            {emojis.length === 0 ? <span className="text-[11px] text-slate-600 px-1">Clique en dessous pour ajouter</span> :
              emojis.map((e, i) => (
                <button key={`${e}-${i}`} onClick={() => remove(i)} className="w-8 h-8 rounded-lg bg-white/5 hover:bg-rose-500/20 text-lg leading-none flex items-center justify-center" title="Clique pour retirer">{e}</button>
              ))}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Choisir</div>
          <div className="grid grid-cols-10 gap-1 max-h-60 overflow-y-auto scrollbar-thin">
            {EMOJI_BANK.map(e => (
              <button key={e} data-testid={`emoji-pick-${e}`} onClick={() => toggle(e)}
                className={`w-8 h-8 rounded-lg text-lg leading-none flex items-center justify-center transition-colors ${emojis.includes(e) ? 'bg-indigo-500/30 ring-1 ring-indigo-400' : 'hover:bg-white/10'}`}>
                {e}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-slate-500">Jusqu&apos;à 10. Clique un emoji dans la banque pour l&apos;ajouter ; clique dans la liste des sélectionnés pour le retirer.</p>
          <Button data-testid="emoji-save-button" className="w-full bg-indigo-500 hover:bg-indigo-600" onClick={() => onSave(emojis.slice(0, 10))}>Enregistrer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AddToListDialog({ open, conv, smartLists, manualLists, onClose, onSave }) {
  const [selected, setSelected] = useState([]);
  useEffect(() => { if (open && conv) setSelected(conv.lists || []); }, [open, conv]);
  const smartIds = smartLists.map(l => l.id);
  // ONE-CLICK: clicking a list toggles + saves IMMEDIATELY (no Save button). Smart-list
  // memberships are computed automatically and preserved untouched.
  const toggle = (id) => {
    const next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
    setSelected(next);
    onSave([...next.filter(x => !smartIds.includes(x)), ...(conv?.lists || []).filter(x => smartIds.includes(x))]);
  };
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200 max-w-sm">
        <DialogHeader><DialogTitle className="text-white">Listes — {conv?.name}</DialogTitle></DialogHeader>
        <div className="pt-2 space-y-2 max-h-[55vh] overflow-y-auto scrollbar-thin">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Clique pour attribuer — enregistré direct</div>
          <div className="space-y-1">
            {manualLists.map(l => {
              const on = selected.includes(l.id);
              return (
                <button key={l.id} data-testid={`list-check-${l.id}`} onClick={() => toggle(l.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-colors text-left ${on ? 'bg-indigo-500/15 border-indigo-500/40' : 'bg-white/[0.02] border-white/10 hover:bg-white/5'}`}>
                  <span className={`w-4 h-4 rounded-md flex items-center justify-center shrink-0 ${on ? 'bg-indigo-500' : 'border border-white/20'}`}>{on && <Check size={11} className="text-white" />}</span>
                  <span className="text-sm text-slate-100">{l.name}</span>
                  <span className="ml-auto text-[10px] text-slate-600">{l.count}</span>
                </button>
              );
            })}
            {manualLists.length === 0 && <div className="text-[11px] text-slate-600 px-2 py-1">Aucune liste manuelle — crée-en une dans Listes.</div>}
          </div>
          <p className="text-[10px] text-slate-600 px-1 pt-1">Les Smart Lists s'attribuent toutes seules selon leurs règles.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ReportSaleDialog({ msg, conv, onClose, onReported }) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (msg) { setAmount(''); setNote(''); } }, [msg]);

  const save = async () => {
    if (!amount || !conv) { toast.error('Montant requis'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/sales', {
        account_id: conv.account_id,
        conversation_id: conv.id,
        chatter_id: msg?.chatter_id || null,
        client: conv.name,
        amount: parseFloat(amount),
        type: 'purchase',
        note: note || null,
        source: 'manual_report',
        link: msg?.media?.name || null,
      });
      toast.success(`Vente de ${amount} € signalée — en attente de validation`);
      onReported(data);
    } catch (e) { toast.error(apiError(e, 'Impossible de signaler la vente')); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!msg} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200 max-w-sm">
        <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><Euro size={16} className="text-emerald-400" /> Signaler une vente</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          {msg && (
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-xs text-slate-400">
              <div className="text-[9px] uppercase tracking-wider text-slate-600 mb-1">Contexte du message · {msg.time}</div>
              <div className="line-clamp-2 italic">"{msg.text || msg.media?.name}"</div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Montant (€)</Label>
            <Input data-testid="report-sale-amount-input" type="number" value={amount} onChange={e => setAmount(e.target.value)} className="bg-white/5 border-white/10 text-white h-10" placeholder="25" autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Note (optionnel)</Label>
            <Input data-testid="report-sale-note-input" value={note} onChange={e => setNote(e.target.value)} className="bg-white/5 border-white/10 text-white h-10" placeholder="ex. paiement vidéo personnalisée" />
          </div>
          <Button data-testid="report-sale-confirm" className="w-full bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold" onClick={save} disabled={saving}>{saving ? 'Signalement...' : 'Confirmer la vente'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
